import { useAuthStore, useData } from "../../../runtime/provider";
import { useQuery } from "@tanstack/react-query";
import {
  unlockedPrivateSongsQuery,
  unlockedPrivateSongsQueryKey,
  USER_LISTUNLOCKEDPRIVATESONGS_QUERY_KEY,
} from "./queryFunctions";

/**
 * useListUnlockedPrivateSongs — list of song ids the requesting user has
 * unlocked via private/access purchases. Used to gate paywalled songs in
 * carousels, search results, the player runtime, and the artist storefront.
 *
 * Backed by the durable-cache layer (`utils/durableCache`): the queryFn
 * writes to SQLite on success and reads on failure, so the unlocked-song
 * list paints from disk on cold start (paywall gating resolves instantly)
 * and stays visible during bad-internet refetches. Auth-gated: the queryFn
 * throws `NoAuthError` when there is no signed-in firebase user, which
 * preserves the pre-migration `enabled: !!firebaseUser` behaviour.
 *
 * Reads only the firebase user rather than the full `useAuth()`: song rows
 * call this per row, and `useAuth()` also mounts the auth-endpoint query and
 * two mutations, which made long lists stutter while scrolling. Selecting the
 * user alone also keeps auth status changes from re-rendering every row.
 */
export const useListUnlockedPrivateSongs = () => {
  const data = useData();
  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const { data: unlockedSongIds } = useQuery({
    ...unlockedPrivateSongsQuery(data),
    enabled: !!firebaseUser,
  });
  return unlockedSongIds;
};

export {
  unlockedPrivateSongsQueryKey,
  USER_LISTUNLOCKEDPRIVATESONGS_QUERY_KEY,
};
